import { World } from '../model/world';
import { Graphics } from '../display/graphics';
import { GridDisplay } from '../display/grid-display';
import { ShipDisplay } from '../display/ship-display';
import { ReadoutUpdate } from './game-update/readout-update';
import { hLineByMidpoint, vLineByMidpoint } from '../math/line';
import { Point } from '../math/point';
import { Color, WHITE } from '../display/color';
import { WHITE_LINE, GRAY_LINE, LIGHT_WHITE_LINE, DARK_GRAY_LINE, DARK_BLUE_LINE, LineStyle } from '../display/lines';

export class GameUpdateState {
  _drawInitialized: boolean = false

  _context: GameUpdate

  changeState(world: World, newState: GameUpdateState, timeRemaining: number = 0): World {
    const world0 = this.exitState(world)
    this._context._changeState(newState)
    const world1 = newState.enterState(world0)

    if (timeRemaining > 0) {
      return newState.update(world1, timeRemaining)
    }
    return world1
  }

  enterState(world: World): World { return world }

  update(world: World, elapsed: number): World { return world }

  initialDraw(world: World, graphics: Graphics): void { }

  draw(world: World, graphics: Graphics): void { }

  exitState(world: World): World { return world }

  completed(world: World): World {
    return this._context.completed(world)
  }
}

export class GameUpdate {
  protected currentState: GameUpdateState

  // TODO: check if can be written as a method
  onComplete: (world: World) => World = (world) => world

  constructor(readonly initalState: GameUpdateState, readonly globalState?: GameUpdateState) {
    if (this.globalState !== undefined) {
      this.globalState._context = this
    }
    this._changeState(initalState)
  }

  update(world: World, elapsed: number): World {
    const world0 = this.globalState?.update(world, elapsed) ?? world
    return this.currentState.update(world0, elapsed)
  }

  draw(world: World, graphics: Graphics): void {
    if (this.globalState !== undefined) {
      drawState(world, graphics, this.globalState)
    }
    drawState(world, graphics, this.currentState)
  }

  _changeState(newState: GameUpdateState): void {
    newState._context = this
    this.currentState = newState
  }

  completed(world: World): World {
    return this?.onComplete(world) ?? world
  }
}

function drawState(world: World, graphics: Graphics, state: GameUpdateState): void {
  if (state._drawInitialized === false) {
    state._drawInitialized = true
    state.initialDraw(world, graphics)
  }
  state.draw(world, graphics)
}

export class GameUpdateGlobalState extends GameUpdateState {
  constructor(readonly readoutUpdate: ReadoutUpdate) {
    super()
  }

  enterState(world: World): World {
    return world
  }

  update(world: World, elapsed: number): World {
    const world0 = this.readoutUpdate.update(world, elapsed)
    return world0.update(elapsed)
  }

 draw(world: World, graphics: Graphics): void {
    graphics.phaserGraphics.clear()
    const gridDisplay = new GridDisplay(graphics)
    gridDisplay.drawGrid(world.grid)

    this.readoutUpdate.draw(world, graphics)

    // const fill = new Color(0xFF, 0xFF, 0xFF, 0.15)
    // graphics.fillStyle(fill)
    // graphics.square(p0)

    // graphics.circle(p0, 1)

    const shipDisplay = new ShipDisplay(graphics)
    const entities = [world.ship, ...world.enemies]
    entities.forEach(e => shipDisplay.draw(e))
  }
}

export class GameUpdateParentState extends GameUpdateState {
  constructor(readonly gameUpdateChild: GameUpdate, readonly nextState?: GameUpdateState) {
    super()
    if (nextState !== undefined) {
      this.gameUpdateChild.onComplete = (world: World) => this.changeState(world, nextState)
    }
  }

  update(world: World, elapsed: number): World {
    return this.gameUpdateChild.update(world, elapsed)
  }

  draw(world: World, graphics: Graphics): void {
    this.gameUpdateChild.draw(world, graphics)
  }
}

export class GameUpdateListState extends GameUpdateState {
  protected gameUpdateList: GameUpdate[]
  constructor(protected gameUpdateListGenerator: (world: World) => GameUpdate[], readonly nextState?: GameUpdateState) {
    super()
  }

  enterState(world: World): World {
    this.gameUpdateList = this.gameUpdateListGenerator(world)
    this.gameUpdateList.forEach(gameUpdate => {
      const _onComplete = gameUpdate.onComplete
      gameUpdate.onComplete = (world) => {
        const world0 = _onComplete(world)
        this.gameUpdateList = this.gameUpdateList.filter(g => g !== gameUpdate)
        return world0
      }
    })
    return world
  }

  update(world: World, elapsed: number): World {
    if (this.gameUpdateList.length === 0) {
      if (this.nextState !== undefined) {
        return this.changeState(world, this.nextState)
      }
      return this.completed(world)
    }

    const world0 = this.gameUpdateList.reduce((accWorld, gameUpdate) => gameUpdate.update(accWorld, elapsed), world)

    return world0
  }

  draw(world: World, graphics: Graphics): void {
    this.gameUpdateList.forEach(gameTurn => {
      gameTurn.draw(world, graphics)
    })
  }
}
